import { Helmet } from 'react-helmet-async';
import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'motion/react';
import { ArrowRight, Compass, Shield, Users, Target, Anchor, Wind, Award } from 'lucide-react';
import { Link } from 'react-router-dom';
import { useBrand } from '../context/BrandContext';
import UnifiedBookingCalendar from './UnifiedBookingCalendar';
import { collection, query, onSnapshot } from 'firebase/firestore';
import { db } from '../firebase';

export default function Coaching() {
  const { coachingPlans, resolveMediaUrl } = useBrand();
  const [instructors, setInstructors] = useState<any[]>([]);
  const [activeCalendarType, setActiveCalendarType] = useState<'coaching' | 'group-lesson'>('coaching');
  const [selectedBookingType, setSelectedBookingType] = useState<'coaching' | 'group-lesson'>('coaching');

  useEffect(() => {
    const q = query(collection(db, 'instructors'));
    const unsub = onSnapshot(q, (snap) => {
      const list = snap.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      list.sort((a: any, b: any) => (a.order ?? 0) - (b.order ?? 0));
      setInstructors(list);
    }, (error) => {
      console.error("Failed to load instructors list", error);
    });
    return unsub;
  }, []);

  const activeInstructors = instructors.length > 0 ? instructors : [
    {
      name: "Taras V.",
      title: "LEAD INSTRUCTOR & COACH",
      initials: "TV",
      image: "",
      achievements: [
        "Crossed Lake Ontario on iSup with no motor boat support — three times.",
        "Completed 110 km international expedition."
      ],
      credentials: [
        "🥋 Black Belt in Judo",
        "NCCP Certified Coach",
        "Safe Sport Certified",
        "Emergency Action Plan Certified",
        "Mental Health in Sport Integration"
      ]
    },
    {
      name: "Mike",
      title: "INSTRUCTOR IN TRAINING",
      initials: "M",
      image: "",
      achievements: [
        "Crossed Lake Ontario on paddle board without support boats."
      ],
      credentials: [
        "🥋 Judo Brown Belt",
        "🥋 BJJ Brown Belt"
      ]
    }
  ];

  const scrollToCalendar = () => {
    document.getElementById('coaching-calendar')?.scrollIntoView({ behavior: 'smooth' });
  };

  const currentPlans = coachingPlans && coachingPlans.length > 0 ? coachingPlans : [
    {
      id: 'private-coaching',
      title: 'Private Coaching',
      description: 'One-on-one intensive focus on stroke mechanics, wind-reading, and efficiency.',
      price: 80,
      interval: '/hr',
      details: '2hr session ($80 to start - 1 free hour)',
      features: ['1 on 1 instruction', 'Premium equipment included', 'Digital waiver and photo gallery'],
      icon: 'Compass',
      type: 'private'
    },
    {
      id: 'group-session',
      title: 'Group Session',
      description: 'Learn the fundamentals together on the water. Great for friends, families, and teams.',
      price: 80,
      interval: 'per person',
      details: '3 hours (2hrs + 1hr free)',
      features: ['6 people maximum', 'Hardware rentals available ($50)', 'Digital waiver and photo gallery'],
      icon: 'Users',
      type: 'group'
    }
  ];

  return (
    <div className="pt-44 md:pt-48 pb-24 bg-ows-white min-h-screen relative overflow-hidden">
      <Helmet>
        <title>Paddle Board Training & Coaching | Toronto Waterfront | OWS</title>
        <meta name="description" content="Master open water mechanics, navigation, and endurance. From private coaching to group training, build your skills with Lake Ontario crossing veterans." />
      </Helmet>

      {/* Background aesthetics */}
      <div className="absolute inset-0 pointer-events-none opacity-[0.02]">
        <div className="absolute inset-0 bg-[linear-gradient(rgba(18,52,59,0.1)_1px,transparent_1px),linear-gradient(90deg,rgba(18,52,59,0.1)_1px,transparent_1px)] bg-[size:100px_100px]" />
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">

        {/* Promotion Banner */}
        <div className="text-center mb-24 relative max-w-4xl mx-auto">
          <div className="inline-flex items-center gap-3 text-ows-teal mb-6">
            <Anchor className="h-5 w-5" />
            <span className="text-[10px] font-bold tracking-[0.4em] uppercase">The Proving Ground</span>
          </div>
          <h1 className="text-4xl md:text-6xl lg:text-7xl font-display font-light uppercase tracking-widest text-ows-dark mb-8 leading-tight">
            Master the Mechanics <br />
            <span className="italic text-ows-teal font-normal lowercase tracking-normal">of open water.</span>
          </h1>
          <p className="text-lg md:text-xl text-ows-dark/60 font-serif italic max-w-2xl mx-auto leading-relaxed">
            Move past casual paddling. From precision coiled navigation sessions to multi-day open-water lake crossings, our training prepares your mind, body, and vessel for the realities of changing wind vectors, heavy commercial wake, and deep-water endurance. If you can master the craft, you can command any horizon.
          </p>
        </div>

        {/* Feature grid explaining more */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-32 border-y border-ows-teal/10 py-16">
          <div className="text-center md:text-left">
            <Target className="h-8 w-8 text-ows-teal mb-6 mx-auto md:mx-0" />
            <h3 className="text-lg font-bold uppercase tracking-widest text-ows-dark mb-4">Precision Form</h3>
            <p className="text-sm text-ows-dark/60 font-light leading-relaxed">Every wasted stroke is lost energy. We break down your mechanics, focusing on core engagement, catch precision, and blade angle to maximize your glide on expedition-scale trips.</p>
          </div>
          <div className="text-center md:text-left">
            <Wind className="h-8 w-8 text-ows-teal mb-6 mx-auto md:mx-0" />
            <h3 className="text-lg font-bold uppercase tracking-widest text-ows-dark mb-4">Wind & Navigation</h3>
            <p className="text-sm text-ows-dark/60 font-light leading-relaxed">Learn to read the water before you touch it. We teach tactile wind reading, plotting vectors, evaluating micro-weather patterns, and making fast coiled decisions during squalls.</p>
          </div>
          <div className="text-center md:text-left">
            <Shield className="h-8 w-8 text-ows-teal mb-6 mx-auto md:mx-0" />
            <h3 className="text-lg font-bold uppercase tracking-widest text-ows-dark mb-4">Survival Redundancy</h3>
            <p className="text-sm text-ows-dark/60 font-light leading-relaxed">The gear doesn't save you; knowing how to use it does. Master self-rescue, tow systems, VHF radio operations, and cold-water shock procedures. Train for the worst, paddle the best.</p>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-12 max-w-5xl mx-auto mb-20">
          {currentPlans.map((plan, idx) => {
            const isDark = plan.type === 'group';
            const isGroup = plan.type === 'group';
            const IconComponent = plan.icon === 'Compass' ? Compass : Users;

            return (
              <motion.div
                key={plan.id}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: idx * 0.1 }}
                className={isDark
                  ? "bg-ows-dark p-12 rounded-[2rem] border border-white/10 hover:border-ows-teal/40 transition-all text-white flex flex-col h-full relative overflow-hidden group shadow-2xl"
                  : "bg-white p-12 rounded-[2rem] border border-ows-teal/20 hover:border-ows-teal/60 transition-all flex flex-col h-full relative overflow-hidden group shadow-2xl"
                }
              >
                {/* Visual Accent */}
                <div className={`absolute top-0 right-0 w-32 h-32 blur-3xl opacity-20 group-hover:opacity-40 transition-opacity ${isDark ? 'bg-ows-teal' : 'bg-ows-orange'}`}></div>

                <div className={isDark
                  ? "h-16 w-16 bg-white/10 flex items-center justify-center rounded-2xl mb-8 relative z-10"
                  : "h-16 w-16 bg-ows-dark text-white flex items-center justify-center rounded-2xl mb-8 relative z-10"
                }>
                  <IconComponent className={isDark ? "h-8 w-8 text-ows-teal" : "h-8 w-8 text-white"} />
                </div>

                <h2 className={isDark
                  ? "text-2xl lg:text-3xl font-display font-light tracking-widest uppercase mb-4 relative z-10"
                  : "text-2xl lg:text-3xl font-display font-light tracking-widest uppercase mb-4 text-ows-dark relative z-10"
                }>{plan.title}</h2>

                <p className={isDark ? "text-white/60 font-serif italic mb-8 min-h-[60px] relative z-10" : "text-ows-dark/60 font-serif italic mb-8 min-h-[60px] relative z-10"}>
                  {plan.description}
                </p>

                <div className={isDark
                  ? "text-4xl lg:text-5xl font-mono uppercase tracking-widest mb-10 relative z-10 text-white"
                  : "text-4xl lg:text-5xl font-mono uppercase tracking-widest mb-10 text-ows-dark relative z-10"
                }>
                  ${plan.price}<span className={isDark ? "text-sm text-white/40 tracking-normal" : "text-sm text-ows-dark/40 tracking-normal"}> {plan.interval}</span>
                  <p className={isDark ? "text-[10px] text-ows-teal tracking-widest uppercase mt-3 font-bold" : "text-[10px] text-ows-teal tracking-widest uppercase mt-3 font-bold"}>{plan.details}</p>
                </div>

                <div className="flex-grow relative z-10">
                  <h4 className={isDark ? "text-[10px] font-bold tracking-widest text-white/40 uppercase mb-4" : "text-[10px] font-bold tracking-widest text-ows-dark/40 uppercase mb-4"}>INCLUDES:</h4>
                  <ul className={isDark ? "space-y-4 mb-10 text-sm font-light text-white/80" : "space-y-4 mb-10 text-sm font-light text-ows-dark/80"}>
                    {plan.features.map((feature, fIdx) => (
                      <li key={`feature-${plan.id}-${fIdx}`} className="flex items-start gap-4">
                        <Shield className="h-4 w-4 text-ows-teal shrink-0 mt-0.5" />
                        <span className="leading-relaxed">{feature}</span>
                      </li>
                    ))}
                  </ul>
                </div>

                <button
                  onClick={() => {
                    // Set the dynamic type before scrolling down
                    setSelectedBookingType(isGroup ? 'group-lesson' : 'coaching');
                    scrollToCalendar();
                  }}
                  className={isDark
                    ? "w-full py-5 bg-white text-ows-dark font-bold tracking-[0.2em] uppercase text-[10px] hover:bg-ows-teal hover:text-white transition-all rounded-full flex items-center justify-center gap-3 relative z-10"
                    : "w-full py-5 bg-ows-dark text-white font-bold tracking-[0.2em] uppercase text-[10px] hover:bg-ows-teal transition-all rounded-full flex items-center justify-center gap-3 relative z-10"
                  }
                >
                  Schedule Your Session <ArrowRight className="h-4 w-4" />
                </button>
              </motion.div>
            )
          })}
        </div>

        {/* Meet the Head Instructors */}
        <div className="mt-32 border-t border-ows-teal/10 pt-24">
          <div className="text-center mb-16">
            <div className="inline-flex items-center gap-3 text-ows-teal mb-4">
              <Award className="h-5 w-5" />
              <span className="text-[10px] font-bold tracking-[0.4em] uppercase font-mono">Elite Leadership</span>
            </div>
            <h2 className="text-3xl font-display uppercase tracking-widest text-[#0D3A40] mb-4">INSTRUCTORS</h2>
            <p className="text-ows-dark/60 font-serif italic max-w-xl mx-auto">
              Our training is developed and led by seasoned open-water crossing veterans — three unsupported Lake Ontario crossings and a 110km international expedition — who bring professional coaching to every session.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-12 max-w-5xl mx-auto">
            {activeInstructors.map((inst, idx) => (
              <div key={idx} className="bg-white p-10 rounded-[2.5rem] border border-ows-teal/10 hover:border-ows-teal/30 hover:shadow-2xl hover:shadow-ows-teal/5 transition-all duration-500 relative flex flex-col justify-between">
                <div>
                  <div className="flex items-center gap-4 mb-8">
                    <div className="w-14 h-14 bg-ows-dark text-white rounded-2xl flex items-center justify-center overflow-hidden border border-ows-teal/10 shadow-sm">
                      {inst.image ? (
                        <img src={resolveMediaUrl(inst.image)} alt={inst.name} className="w-full h-full object-cover" />
                      ) : (
                        <span className="font-display font-bold text-lg">{inst.initials || inst.name.substring(0, 2).toUpperCase()}</span>
                      )}
                    </div>
                    <div>
                      <h3 className="text-xl font-bold uppercase tracking-wider text-ows-dark">{inst.name}</h3>
                      <p className="text-xs text-ows-teal uppercase tracking-widest font-mono font-bold mt-1">{inst.title}</p>
                    </div>
                  </div>

                  <div className="space-y-6">
                    {inst.achievements?.length > 0 && (
                      <div>
                        <div className="text-[10px] font-bold uppercase tracking-wider text-ows-dark/40 mb-2 font-mono">Expedition Achievements</div>
                        <ul className="space-y-3">
                          {inst.achievements.map((ach: string, aIdx: number) => (
                            <li key={aIdx} className="flex items-start gap-3 text-sm text-ows-dark/80 font-light">
                              <span className="text-ows-teal font-bold mt-0.5">•</span>
                              <span>{ach}</span>
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}

                    {inst.credentials?.length > 0 && (
                      <div>
                        <div className="text-[10px] font-bold uppercase tracking-wider text-ows-dark/40 mb-2 font-mono">Professional & Martial Credentials</div>
                        <div className="flex flex-wrap gap-2">
                          {inst.credentials.map((cred: string, cIdx: number) => {
                            const isBlackBelt = cred.toLowerCase().includes('black belt');
                            return (
                              <span
                                key={cIdx}
                                className={`px-3 py-1.5 text-[9px] font-mono tracking-wider uppercase rounded-md font-bold leading-none ${isBlackBelt
                                  ? "bg-[#141414] text-white border border-white/10 shadow-sm"
                                  : "bg-ows-sand text-ows-dark border border-ows-teal/15"
                                  }`}
                              >
                                {cred}
                              </span>
                            );
                          })}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        <div id="coaching-calendar" className="mt-32 pt-16">
          <div className="text-center mb-16">
            <h2 className="text-3xl font-display uppercase tracking-widest text-ows-dark mb-4">Book Your Training</h2>
            <p className="text-ows-dark/60 font-serif italic max-w-xl mx-auto mb-16">Select a time from our live training schedule. We typically train outbound from the Leslie Street Spit.</p>

            {/* Colour Grading Explanation */}
            <div className="max-w-3xl mx-auto text-left bg-ows-grey p-8 rounded-3xl border border-ows-teal/10">
              <div className="flex items-center gap-3 mb-6">
                <Compass className="h-5 w-5 text-ows-teal" />
                <h3 className="text-sm font-bold uppercase tracking-widest text-ows-dark">Dynamic Difficulty Grading</h3>
              </div>
              <p className="text-sm text-ows-dark/60 font-light leading-relaxed mb-8">
                Our session difficulty levels are dynamically calculated based on real-time marine forecasts, wave heights, and wind speeds. To guarantee your safety and the integrity of the group, access to advanced conditions is restricted by your membership level.
              </p>

              <div className="space-y-4">
                <div className="flex items-center justify-between p-4 bg-white rounded-xl border border-green-500/20">
                  <div className="flex items-center gap-4">
                    <span className="h-3 w-3 rounded-full bg-green-500 shadow-[0_0_10px_rgba(34,197,94,0.5)]"></span>
                    <div>
                      <div className="text-[10px] font-bold tracking-widest uppercase text-ows-dark">Green Level // Routine</div>
                      <div className="text-xs text-ows-dark/50 font-serif italic mt-1">Calm winds. Standard coiled training operations.</div>
                    </div>
                  </div>
                  <span className="text-[10px] uppercase font-bold tracking-wider text-green-600 px-2 py-1 bg-green-50 rounded">Open to All</span>
                </div>

                <div className="flex items-center justify-between p-4 bg-white rounded-xl border border-blue-500/20">
                  <div className="flex items-center gap-4">
                    <span className="h-3 w-3 rounded-full bg-blue-500 shadow-[0_0_10px_rgba(59,130,246,0.5)]"></span>
                    <div>
                      <div className="text-[10px] font-bold tracking-widest uppercase text-ows-dark">Blue Level // Elevated</div>
                      <div className="text-xs text-ows-dark/50 font-serif italic mt-1">Active water. Resistance training and moderate chop.</div>
                    </div>
                  </div>
                  <span className="text-[10px] uppercase font-bold tracking-wider text-blue-600 px-2 py-1 bg-blue-50 rounded">Open to All</span>
                </div>

                <div className="flex items-center justify-between p-4 bg-white rounded-xl border border-amber-500/20">
                  <div className="flex items-center gap-4">
                    <span className="h-3 w-3 rounded-full bg-amber-500 shadow-[0_0_10px_rgba(245,158,11,0.5)]"></span>
                    <div>
                      <div className="text-[10px] font-bold tracking-widest uppercase text-ows-dark">Brown Level // Adverse</div>
                      <div className="text-xs text-ows-dark/50 font-serif italic mt-1">Heavy winds. Demanding physical requirements.</div>
                    </div>
                  </div>
                  <span className="text-[10px] uppercase font-bold tracking-wider text-amber-600 px-2 py-1 bg-amber-50 rounded border border-amber-200">Syndicate Member Only</span>
                </div>

                <div className="flex flex-col sm:flex-row sm:items-center justify-between p-4 bg-[#151515] rounded-xl border border-white/10 gap-4">
                  <div className="flex items-center gap-4">
                    <span className="h-3 w-3 rounded-full bg-gray-400 shadow-[0_0_10px_rgba(156,163,175,0.5)]"></span>
                    <div>
                      <div className="text-[10px] font-bold tracking-widest uppercase text-white">Black Level // Challenger</div>
                      <div className="text-xs text-white/50 font-serif italic mt-1">Extreme chop. Night sessions. Intense physical endurance required.</div>
                    </div>
                  </div>
                  <span className="text-[10px] uppercase font-bold tracking-wider text-white px-2 py-1 bg-white/10 rounded border border-white/20 whitespace-nowrap">Elite Rank Only</span>
                </div>

                <div className="flex flex-col sm:flex-row sm:items-center justify-between p-4 bg-[#050505] rounded-xl border border-red-500/30 gap-4">
                  <div className="flex items-center gap-4">
                    <span className="h-3 w-3 rounded-full bg-red-500 shadow-[0_0_10px_rgba(239,68,68,0.5)] animate-pulse"></span>
                    <div>
                      <div className="text-[10px] font-bold tracking-widest uppercase text-white">Red Level // Apex</div>
                      <div className="text-xs text-red-200/50 font-serif italic mt-1">Gale-force conditions. Absolute survival mechanics. Not for the faint of heart.</div>
                    </div>
                  </div>
                  <span className="text-[10px] uppercase font-bold tracking-wider text-red-400 px-2 py-1 bg-red-500/10 rounded border border-red-500/30 whitespace-nowrap">Elite Rank Only</span>
                </div>
              </div>
            </div>
          </div>
          {/* Calendar Selection Tabs */}
          <div className="flex justify-center gap-4 mb-10">
            <button
              type="button"
              onClick={() => setSelectedBookingType('coaching')}
              className={`px-6 py-3 rounded-full text-xs font-bold tracking-widest uppercase transition-all ${selectedBookingType === 'coaching' ? 'bg-ows-teal text-white' : 'bg-ows-grey text-ows-dark/60'
                }`}
            >
              Private 1-on-1
            </button>
            <button
              type="button"
              onClick={() => setSelectedBookingType('group-lesson')}
              className={`px-6 py-3 rounded-full text-xs font-bold tracking-widest uppercase transition-all ${selectedBookingType === 'group-lesson' ? 'bg-ows-teal text-white' : 'bg-ows-grey text-ows-dark/60'
                }`}
            >
              Group Sessions
            </button>
          </div>
          <UnifiedBookingCalendar
            type={selectedBookingType}
            title={selectedBookingType === 'coaching' ? "Private Coaching Roster" : "Group Training Roster"}
            subtitle="SCHEDULE"
            description={
              selectedBookingType === 'coaching'
                ? "Select an available 1-on-1 private training window."
                : "Secure your place in an upcoming group roster session."
            }
          />
        </div>

      </div>
    </div>
  );
}
